import axios from 'axios';

// Resolve API base URL (Vite env preferred)
const resolvedBaseURL =
  import.meta?.env?.VITE_API_BASE_URL ||
  (import.meta.env.PROD
    ? 'https://wasser-pro-api.sherhan1988hp.workers.dev'
    : ''); // Use Vite proxy in dev (baseURL empty, paths start with /api)

const resolvedApiOrigin = resolvedBaseURL.replace(/\/api\/?$/, '');

// Create axios instance
const api = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable httpOnly cookie handling
});

// SECURITY: Access token stored in memory only (not localStorage)
// This prevents XSS attacks from stealing tokens
let inMemoryAccessToken = null;

// Token refresh coordination
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const authService = {
  // SECURITY: Set access token in memory only (not localStorage)
  // Refresh token is handled via httpOnly cookie by backend
  setTokens: (accessToken, _refreshToken) => {
    if (accessToken) {
      inMemoryAccessToken = accessToken;
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
    // Note: refreshToken should be set as httpOnly cookie by backend
    // We don't store it on the client side anymore
  },

  // Get current access token (for components that need it)
  getAccessToken: () => inMemoryAccessToken,

  // Remove tokens
  clearTokens: () => {
    inMemoryAccessToken = null;
    delete api.defaults.headers.common['Authorization'];
    // Server should clear httpOnly cookie on logout
  },

  // Force a token refresh using httpOnly cookie
  // Returns a promise that resolves to new accessToken
  refreshTokens: async () => {
    // Refresh token is sent automatically via httpOnly cookie
    const resp = await api.post('/api/auth/refresh');
    const { accessToken } = resp.data || {};
    if (!accessToken)
      throw new Error('Refresh failed: no accessToken returned');
    authService.setTokens(accessToken);
    return accessToken;
  },

  // Auth API wrappers
  register: (userData) => api.post('/api/auth/register', userData),
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  logout: () => api.post('/api/auth/logout'),
  refresh: (refreshToken) => api.post('/api/auth/refresh', { refreshToken }),
  getCurrentUser: () => api.get('/api/auth/me'),
  updateProfile: (profileData) => api.put('/api/auth/profile', profileData),
  applyForB2B: (companyData) => api.post('/api/auth/apply-b2b', companyData),
  getB2BApplications: () => api.get('/api/auth/admin/b2b-applications'),
  approveB2BApplication: (userId, approvalData) =>
    api.post(`/api/auth/admin/approve-b2b/${userId}`, approvalData),
  rejectB2BApplication: (userId) =>
    api.post(`/api/auth/admin/reject-b2b/${userId}`),
};

// Request interceptor: attach access token and CSRF token
api.interceptors.request.use(
  (config) => {
    // Use in-memory token instead of localStorage
    if (inMemoryAccessToken) {
      config.headers.Authorization = `Bearer ${inMemoryAccessToken}`;
    }

    // CSRF protection: attach token from meta tag
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 by attempting refresh once and retrying queued requests
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Don't retry refresh endpoint itself to avoid infinite loop
    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');

    // If we get 401: try to refresh (only once per original request)
    // Refresh token is sent automatically via httpOnly cookie
    if (status === 401 && !originalRequest._retry && !isRefreshRequest) {
      if (isRefreshing) {
        // Queue the request until refresh completes
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        authService
          .refreshTokens()
          .then((newToken) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
            processQueue(null, newToken);
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            authService.clearTokens();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // Attach server message if present
    const serverMessage =
      error.response?.data?.error || error.response?.data?.message;
    if (serverMessage) {
      error.message = serverMessage;
      error.errorMessage = serverMessage;
    }
    return Promise.reject(error);
  }
);

export const API_BASE_URL = resolvedBaseURL;
export const API_ORIGIN = resolvedApiOrigin;

export default authService;
export { api };
